/**
 * Program: Router
 * Author: SDL
 * Description: router setting file
 */
import Vue from 'vue';
import Router from 'vue-router';
import _ from 'lodash';
import axios from 'axios';
import store from '@/vuex/store';
import { fetchMenuAll } from '@/service/menuService';
import SDLUtil from '@/utils/SDLUtil';
import { i18n, loadLanguageAsync } from '../i18n';
import LoginPage from '@/components/view/admin/main/LoginPage';
import MainPage from '@/components/view/admin/main/MainPage';
import MainIndexPage from '@/components/view/admin/main/MainIndexPage';
import EmptyPage from '@/components/view/admin/main/EmptyPage';
import MarkupRoutes from './markupRouter';

Vue.use(Router);

export const router = new Router({
  mode: 'history',
  routes: [
    {
      name: 'login',
      path: `${SDLUtil.WEB_CONTEXT_PATH}/login`,
      component: LoginPage,
    },
    {
      name: 'emptyPage',
      path: `${SDLUtil.WEB_CONTEXT_PATH}/emptypage`,
      component: EmptyPage,
    },
    {
      name: '404',
      path: `${SDLUtil.WEB_CONTEXT_PATH}/404`,
      component: EmptyPage,
    },
    {
      name: 'error',
      path: `${SDLUtil.WEB_CONTEXT_PATH}/error`,
      component: EmptyPage,
    },
    {
      path: `${SDLUtil.WEB_CONTEXT_PATH}/noauth`,
      component: EmptyPage,
    },
    {
      path: `${SDLUtil.WEB_CONTEXT_PATH}/`,
      meta: 'main',
      component: MainPage,
      children: [
        {
          name: 'mainIndexPage',
          path: '',
          component: MainIndexPage,
        },
      ],
    },
    {
      path: '*',
      // redirect: `${SDLUtil.WEB_CONTEXT_PATH}/404`,
      component: EmptyPage,
    },
    MarkupRoutes,
  ],
});

/**
 * @name Navigation_Guardian_1
 * Add to menu ID, page ID header when changing router
 */
 router.beforeEach((to, from, next) => {
  if (to.meta.menuId) {
    axios.defaults.headers.common['menu-id'] = to.meta.menuId;
  } else {
    delete axios.defaults.headers.common['menu-id'];
  }
  if (to.meta.pageId) {
    axios.defaults.headers.common['page-id'] = to.meta.pageId;
  } else {
    delete axios.defaults.headers.common['page-id'];
  }
  if (to.query.token) {
    let token = to.query.token;
    localStorage.setItem('userToken', `${token}`);
    localStorage.setItem('user', JSON.stringify(SDLUtil.parseJwt(token)));
  }
  axios.defaults.headers.common['x-auth-token'] = localStorage.getItem('userToken');

  return next();
});

/**
 * @name Navigation_Guardian_2
 * Language Settings When Changing Router
 */
router.beforeEach((to, from, next) => {
  if (store.getters.getLocale !== '') {
    i18n.locale = store.getters.getLocale;
  } else {
    i18n.locale = 'ko_KR';
  }
  return next();
});

/**
 * @name Navigation_Guardian_3
 * Import locale information from logged in user information
 */
router.beforeEach((to, from, next) => {
  let userPreferLang;
  const userInfo = SDLUtil.getLoginedUserInfo();
  if (userInfo && userInfo.language) {
    userPreferLang = userInfo.language;
  }

  const lang = (userPreferLang || window.navigator.userLanguage || window.navigator.language || SDLUtil.DEFAULT_LANG).replace(
    '-',
    '_',
  );
  loadLanguageAsync(lang).then(() => next());
});

/**
 * @name Navigation_Guardian_4
 * Initialize storing the stored parameter setting value when changing the router
 */
 router.beforeEach((to, from, next) => {
  const loggedIn = SDLUtil.isLogin(); // 로그인여부 확인.
  if (loggedIn && to.path.includes('/error')) {
    return next();
  }
  if (!loggedIn && to.path.includes('/error')) {
    return next();
  }

  if (!loggedIn && to.path !== '/login' && !to.path.includes('/error')) {
    let redicectUrl = to.redirectedFrom;
    return next({ path: `${SDLUtil.WEB_CONTEXT_PATH}/login`, query: { redirect: redicectUrl } });
  }

  if (to.query.token && to.fullPath.indexOf(`${SDLUtil.WEB_CONTEXT_PATH}/login`) !== -1) {
    // AD인증 직후, /login?token=
    // 기존 로컬스토리지가 남아있는 상태에서 /login에 토큰을 물고있다면, 기존 값은 클리어 해준다.
    // localStorage.setItem('userToken', `${to.query.token}`);
    localStorage.setItem('user', '');
    return next(); // 로그인 페이지로 가게된다.
  }

  return next();
});

/**
 * @name Navigation_Guardian_5
 * Initialize storing the stored parameter setting value when changing the router
 */
router.beforeEach(async (to, from, next) => {
  const loggedIn = SDLUtil.isLogin();
  let authInfo = store.getters.getAuthInfo;

  if (to.params.restoreList !== true && to.meta.menuId !== from.meta.menuId) {
    store.commit('PARAMS', {});
  }

  if (to.fullPath.indexOf(`${SDLUtil.WEB_CONTEXT_PATH}/error`) !== -1 && to.query.code) {
    store.commit('API_ERROR_CODE', to.query.code);
  }
  
  if (!loggedIn || to.path.includes('/error')) {
    store.commit('AUTH_INFO', {});
    store.commit('HIGHEST_ROLE', '');
  } 
  if (loggedIn && !to.path.includes('/error')) { // 로그인이고 에러도 아닐때만 한다.
    if (to.path !== '/login'
        && !to.path.includes('/error')
        && ( _.isEmpty(authInfo) 
            || to.fullPath.indexOf(`${SDLUtil.WEB_CONTEXT_PATH}/voc/authreq/list`) !== -1 
            || to.fullPath.indexOf(`${SDLUtil.WEB_CONTEXT_PATH}/voc/vocreq/vocList`) !== -1 )) {
      // 로그인임, 로그인 페이지로 가는것이 아니고 (authInfo가 비었거나 권한신청, Lock해제신청인 경우)
      const res = await axios
                        .get(`${SDLUtil.API_URL}/gcrm/voc/my-auth`)
                        .catch(err => {
                          console.warn(to.path, err);
                          return err.response;
                        });
      if (res.status !== 200) {
        if (res.data.error) {
          return next(`${SDLUtil.WEB_CONTEXT_PATH}/error?code=${res.data.error.code}&redirect=${to.path}`);
        }
      }
      if (res.status === 200) {
        // if (res.data.error) {
        //   return next(`${SDLUtil.WEB_CONTEXT_PATH}/error?code=${res.data.error.code}&redirect=${to.path}`);
        // }
        store.commit('AUTH_INFO', res.data); // 오쓰 정보를 넣는다.
        if (res.data.userInfo && res.data.userInfo.userRoles) {
          let userRoleNames = [];
          localStorage.setItem('isRewardsUser', false);
          res.data.userInfo.userRoles.forEach(item => {
            userRoleNames.push(item.roleName);
            if (item.roleName.includes('RWD')) {
              localStorage.setItem('isRewardsUser', true);
            }
          });
          let highestRole = 'UNKNOWN';
          if (userRoleNames.includes('GC_IT_ADMIN_ROLE')) {
            highestRole = 'GC_IT_ADMIN_ROLE';
          } else if (userRoleNames.includes('GC_ADMIN_ROLE')) {
            highestRole = 'GC_ADMIN_ROLE';
          } else if (userRoleNames.includes('GC_USER_ROLE')) {
            highestRole = 'GC_USER_ROLE';
          } else if (userRoleNames.includes('GC_LOCK_USER_ROLE')) {
            highestRole = 'GC_LOCK_USER_ROLE';
          } else if (userRoleNames.includes('GC_LONGTERM_LOCK_USER_ROLE')) { // handle store data for GC_LONGTERM_LOCK_USER_ROLE -- CR(C23050002148)
            highestRole = 'GC_LONGTERM_LOCK_USER_ROLE';
          } else if (userRoleNames.includes('GC_INITIAL_USER_ROLE')) {
            highestRole = 'GC_INITIAL_USER_ROLE';
          }
          store.commit('HIGHEST_ROLE', highestRole); // 최우선 롤 정보만 따로 저장해둔다.
        }
      } else { // authInfo API 200 아닌경우.
        store.commit('AUTH_INFO', {});
        store.commit('HIGHEST_ROLE', '');
      }
    }
  }




  //rewards : country store data set
  let countryCode = localStorage.getItem('countryCode');
  let countryOffset =localStorage.getItem('countryOffset');
  if (loggedIn 
      && to.path == '/'  
      && from.path == '/'
      && _.isEmpty(countryCode)
      && _.isEmpty(countryOffset) ) { 
      const res = await axios
      .get(`${SDLUtil.API_URL}/auth/rewardsUserInfo`)
      .catch(err => {
        console.warn(to.path, err);
        return err.response;
      });
      if (res.status === 200) {
        localStorage.setItem('countryCode', res.data.countryCode);
        localStorage.setItem('countryOffset', res.data.countryOffset);
        console.log(JSON.stringify((res.data.serviceInfo.map(x => x.serviceInfo))));
        localStorage.setItem('allowPartners',JSON.stringify((res.data.serviceInfo.map(x => x.serviceInfo))));
      }
  }

  //rewards : 



  return next();
});

/**
 * @name Navigation_Guardian_6
 * logined Guardian 로그인된 상태에 대해서만 감응한다.
 */
router.beforeEach((to, from, next) => {
  const loggedIn = SDLUtil.isLogin();
  let highestRole = store.getters.getHighestRole;
  if (loggedIn && to.path !== '/error') {
    // 로그인되었고, 에러도 없는 상황이지만 롤에 따라서는, 홈 대시보드로 오지 못하게 한다.
    if (to.path === '/') {
      if (highestRole === 'GC_LONGTERM_LOCK_USER_ROLE') { // for role GC_LONGTERM_LOCK_USER_ROLE directly redirecting user to raise user lock application page -- CR(C23050002148)
        return next(`${SDLUtil.WEB_CONTEXT_PATH}/voc/vocreq/vocWrite`);
      }
      if (highestRole === 'GC_LOCK_USER_ROLE') {
        return next(`${SDLUtil.WEB_CONTEXT_PATH}/voc/vocreq/vocWrite`);
      }
      if (highestRole === 'GC_INITIAL_USER_ROLE') {
        return next(`${SDLUtil.WEB_CONTEXT_PATH}/voc/authreq/application`);
      }
    }
  }

  return next();
});

router.afterEach(to => { // menu 방문에 대한 히스토리 쌓는 API 호출
  const loggedIn = SDLUtil.isLogin();
  if (loggedIn && to.path !== '/error') {
    let title = SDLUtil.getMsgProp('sdl.gcrm.projectName');
    if (to.meta.labelJson && to.meta.labelJson[i18n.locale]) {
      title += ` - ${to.meta.labelJson[i18n.locale]}`;
    } else if (to.meta.label) {
      title += ` - ${to.meta.label}`;
    }
    document.title = title;
    if (to.meta.menuId !== undefined) {
      axios.post(`${SDLUtil.API_URL}/history/menuUseHistory/${to.meta.menuId}`).then().catch(err => console.warn(err));
    }
  }
});

export async function addEntryPoint(routeData = []) { // router를 만들어준다.
  let isLogin = SDLUtil.isLogin();
  const entryChildren = routeData
    .filter(menu => menu.pagePath)
    .map(menu => ({
      name: menu.pageId,
      path: menu.pagePath,
      redirect: isLogin ? `${SDLUtil.WEB_CONTEXT_PATH}/noauth` : `${SDLUtil.WEB_CONTEXT_PATH}/login?redirect=${window.location.pathname}`, // `${SDLUtil.WEB_CONTEXT_PATH}/noauth`,
      meta: {
        ...menu,
      },
    }));
  const { routes } = router.options;
  const mainRoute = routes.find(r => r.meta === 'main');
  
  if (isLogin) {
    await Promise.all([SDLUtil.loadAllCommCodeList(), store.dispatch('getMenus')]);

    let authPages = [];
    const termsUrl = `${SDLUtil.WEB_CONTEXT_PATH}/terms-conditions`;
    const isTermsPage = window.location.pathname === termsUrl;
    const menuResponse = await fetchMenuAll()
      .then(res => res)
      .catch(err => {
        let errorCode  = err.data.error.code;
        if (errorCode === 201 && !isTermsPage) {
          console.info('router index addEntryPoint - Policy Error : ', err);
          // window.location.href = '/';
          // window.location = `${SDLUtil.WEB_CONTEXT_PATH}/error?code=${err.data.error.code}`;
        }
        return err;
      });

    if (menuResponse.data && !menuResponse.data.error) {
      menuResponse.data.forEach(menu => {
        const pages = menu.pages.map(page => {
          const routeItem = {
            name: page.pageId,
            path: page.pagePath,
            meta: {
              ...menu,
              ...page,
            },
          };
          if (page.componentName !== undefined && page.componentName !== '') {
            routeItem.component = () => import(/* webpackChunkName: '[request]' */ `@/components/view/${page.componentName}`);
          }
          return routeItem;
        });
  
        authPages = authPages.concat(pages);
      });
    }
    
    authPages.forEach(page => {
      const idx = _.findIndex(entryChildren, ['path', page.path]);
      if (idx > -1) {
        entryChildren[idx] = page;
      }
    });
  }
  mainRoute.children = [...entryChildren];
  router.addRoute(mainRoute);
}

router.onError(error => {
  if (/loading chunk \d* failed./i.test(error.message)) {
    window.location.reload();
  }
});

export default router;



// WEBPACK FOOTER //
// ./src/router/index.js